/**
 * Adding somebody to the system.
 *
 * The form carries three rules the API allows you to break, because each one
 * is a mistake that looks like success at the moment it is made and costs
 * something months later.
 *
 * **A first password is generated, not typed.** Whoever creates the account
 * necessarily knows the password they set, and an Admin who knows the Finance
 * Head's password can sign in as them — which would make maker-checker one
 * person wearing two hats. The backend marks it `must_change_password` so it
 * opens nothing but the change-password screen, and generating it here means
 * it is not a password the Admin also uses elsewhere.
 *
 * **An Employee or a Book Keeper must have a home clinic.** Without one their
 * claims and requests carry no location: a Book Keeper scoped to a clinic
 * cannot see them, and the spend lands in Unassigned on every clinic report.
 * Nothing throws. A UAT run walked straight into it, which is why there is
 * both a detector for the condition and this, which stops it being created.
 *
 * **Scoped to nowhere is not scoped.** Visibility `locations` with no clinics
 * chosen fails closed and shows the person nothing at all, so they report the
 * system as broken rather than as misconfigured.
 */

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type Location, type User } from '../api'
import { ROLE_LABELS } from '../auth'
import { ErrorNote, Field, Note } from '../components/ui'

const ROLES = ['admin', 'finance_head', 'book_keeper', 'employee']
const VISIBILITIES = ['self', 'locations', 'all']

/** Roles whose people raise documents that belong to a clinic. */
const NEEDS_A_CLINIC = new Set(['employee', 'book_keeper'])

/**
 * What each role almost always wants, applied when the role changes and
 * overridable afterwards. Visibility is independent of role — two Book Keepers
 * at different clinics hold identical permissions and must not see the same
 * rows — so this is a default, not a consequence.
 */
const USUAL_VISIBILITY: Record<string, string> = {
  admin: 'all',
  finance_head: 'all',
  book_keeper: 'locations',
  employee: 'self',
}

/**
 * Long enough that it is not worth guessing and awkward enough that nobody
 * keeps it. `crypto` rather than `Math.random`, which is not meant for this
 * and is a habit worth not forming.
 */
export function generatePassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  const body = Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')
  // A symbol and a digit are here to satisfy password rules people's own
  // policies tend to impose, not because the entropy needs them.
  return `${body}-7`
}

export default function AddPerson({ locations }: { locations: Location[] }) {
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('employee')
  const [visibility, setVisibility] = useState('self')
  const [home, setHome] = useState('')
  const [scope, setScope] = useState<string[]>([])
  const [password, setPassword] = useState(generatePassword)
  const [created, setCreated] = useState<{ user: User; password: string } | null>(null)

  const create = useMutation({
    mutationFn: () =>
      api.post<User>('/org/users', {
        email: email.trim().toLowerCase(),
        display_name: name.trim(),
        role,
        visibility,
        scope_location_ids: visibility === 'locations' ? scope : [],
        home_location_id: home || null,
        password,
      }),
    onSuccess: (user) => {
      setCreated({ user, password })
      setName('')
      setEmail('')
      setHome('')
      setScope([])
      setPassword(generatePassword())
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const clinicMissing = NEEDS_A_CLINIC.has(role) && !home
  const scopeMissing = visibility === 'locations' && scope.length === 0
  const ready = name.trim() && email.trim().includes('@') && !clinicMissing && !scopeMissing

  const clinics = useMemo(() => locations.filter((l) => l.kind !== 'head_office'), [locations])

  return (
    <>
      <ErrorNote error={create.error} />

      {created && (
        <Note kind="ok">
          <strong>{created.user.display_name}</strong> can now sign in as{' '}
          <span className="mono">{created.user.email}</span> with the one-time password{' '}
          <span className="mono">
            <strong>{created.password}</strong>
          </span>
          . Pass it on however you would pass on a key, and it stops working the moment they choose
          their own. This is the only time it is shown — nothing stores it, and it cannot be looked
          up. If it goes astray before they use it, issue another by opening them in the list.
        </Note>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (ready) create.mutate()
        }}
      >
        <div className="row">
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Email" caption="what they sign in with">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>
        </div>

        <div className="row">
          <Field label="Role">
            <select
              value={role}
              onChange={(e) => {
                setRole(e.target.value)
                setVisibility(USUAL_VISIBILITY[e.target.value] ?? 'self')
              }}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r] ?? r}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Row visibility" caption="which rows, not which screens">
            <select value={visibility} onChange={(e) => setVisibility(e.target.value)}>
              {VISIBILITIES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Home clinic"
            caption={NEEDS_A_CLINIC.has(role) ? 'required for this role' : 'optional for this role'}
          >
            <select value={home} onChange={(e) => setHome(e.target.value)}>
              <option value="">
                {NEEDS_A_CLINIC.has(role) ? 'Choose a clinic…' : 'None — belongs to the group'}
              </option>
              {clinics.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {visibility === 'locations' && (
          <Field
            label="Clinics they can see"
            caption="row visibility, separate from where they work"
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {clinics.map((l) => (
                <label key={l.id} style={{ margin: 0, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={scope.includes(l.id)}
                    onChange={(e) =>
                      setScope((current) =>
                        e.target.checked ? [...current, l.id] : current.filter((id) => id !== l.id),
                      )
                    }
                  />{' '}
                  {l.name}
                </label>
              ))}
            </div>
          </Field>
        )}

        {clinicMissing && (
          <Note kind="warn">
            An {ROLE_LABELS[role]?.toLowerCase() ?? role} with no home clinic raises claims and
            requests that carry no clinic. A Book Keeper scoped to a clinic cannot see them, and
            their spend lands in Unassigned on every clinic report. Nothing fails, which is what
            makes it worth stopping here.
          </Note>
        )}

        {scopeMissing && (
          <Note kind="warn">
            Visibility is <span className="mono">locations</span> with no clinics chosen, which
            means scoped to nowhere. It fails closed: they would see none of their own work and
            report the system as broken.
          </Note>
        )}

        {role === 'admin' && (
          <Note kind="info">
            An Admin configures the company and reads everything, and holds no permission to create,
            approve or release anything financial. Adding one does not add somebody who can approve
            a payment.
          </Note>
        )}

        <Field
          label="First password"
          caption="generated, shown once, replaced on their first sign-in"
        >
          <div className="row">
            <div>
              <input className="mono" value={password} readOnly />
            </div>
            <div className="shrink">
              <button type="button" onClick={() => setPassword(generatePassword())}>
                Generate another
              </button>
            </div>
          </div>
        </Field>

        <button className="primary" type="submit" disabled={!ready || create.isPending}>
          {create.isPending ? 'Adding…' : 'Add this person'}
        </button>
      </form>
    </>
  )
}
